import type { Folder, ItemSummary } from '@mystiq/contracts';
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Copy, Folder as FolderIcon, ICON, Inbox, Meeting, Mic, Note, File as FileIcon, Site, TextItem } from '../icons.ts';
import { formatBytes, formatDuration, kindLabel, processingSummary, timeAgo } from '../lib/format.ts';

type IconComponent = ComponentType<{ size?: number; stroke?: number }>;

export function Thumb({ icon: Icon }: { icon: IconComponent }) {
  return (
    <span className="thumb" aria-hidden>
      <Icon size={ICON.empty} stroke={ICON.stroke} />
    </span>
  );
}

export function iconForItem(item: Pick<ItemSummary, 'kind'> & { capture?: ItemSummary['capture'] }): IconComponent {
  if (item.kind === 'note') return Note;
  if (item.kind === 'site') return Site;
  if (item.kind === 'extracted_text' || item.kind === 'transcript') return TextItem;
  if (item.capture === 'voice_note') return Mic;
  if (item.capture === 'meeting') return Meeting;
  return FileIcon;
}

export type RowSelection = { selected: boolean; onToggle(): void };

export function ItemRow({
  ws,
  item,
  extraMeta,
  actions,
  selection,
}: {
  ws: string;
  item: ItemSummary;
  extraMeta?: string;
  actions?: ReactNode;
  selection?: RowSelection | undefined;
}) {
  const length = item.media ? formatDuration(item.media.durationMs) : null;
  const meta = [kindLabel(item), length ?? (item.kind === 'file' ? formatBytes(item.sizeBytes) : null), processingSummary(item), `Edited ${timeAgo(item.updatedAt)}`, extraMeta]
    .filter(Boolean)
    .join(' · ');
  const body = (
    <>
      <Thumb icon={iconForItem(item)} />
      <span className="row-body">
        <span className="row-title">{item.displayTitle}</span>
        <span className="row-meta">{item.description ? `${meta} · ${item.description}` : meta}</span>
      </span>
    </>
  );
  const check = selection && (
    <input type="checkbox" className="row-check" aria-label={`Select ${item.displayTitle}`} checked={selection.selected} onChange={selection.onToggle} />
  );
  if (actions) {
    return (
      <li className="row">
        {check}
        {body}
        <span className="row-actions">{actions}</span>
      </li>
    );
  }
  return (
    <li className={selection ? 'row-selectable' : undefined}>
      {check}
      <Link className="row" to={`/w/${ws}/items/${item.id}`}>
        {body}
      </Link>
    </li>
  );
}

export function FolderRow({ ws, folder, meta }: { ws: string; folder: Folder; meta: string }) {
  return (
    <li>
      <Link className="row" to={`/w/${ws}/folders/${folder.id}`}>
        <Thumb icon={folder.systemRole === 'inbox' ? Inbox : FolderIcon} />
        <span className="row-body">
          <span className="row-title">{folder.name}</span>
          <span className="row-meta">{meta}</span>
        </span>
      </Link>
    </li>
  );
}

export function EmptyState({ icon: Icon, title, children }: { icon: IconComponent; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <Icon size={ICON.empty} stroke={ICON.stroke} />
      <p className="empty-title">{title}</p>
      {children && <div className="meta">{children}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose(): void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input, select, button')?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <h2 className="modal-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <p role="alert">
      <span className="state">Not done.</span> {message}
    </p>
  );
}

/** A value to copy, such as a link or key, with a Copy button. */
export function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="inline-form">
        <code className="code" style={{ flex: 1 }}>
          {value}
        </code>
        <button
          className="button button-inline"
          onClick={async () => {
            await navigator.clipboard.writeText(value).catch(() => undefined);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          <Copy size={ICON.inline} stroke={ICON.stroke} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
